/**
 * Resolve the DISPLAY NAME of the user who created a row.
 *
 * `notes-c.user_id` (and `patients_with_ehrid.user_id`) is the row owner — the
 * account that inserted it (AddPatientModal sets it at insert time). It is a
 * bare uuid, so anything user-facing has to join it to `users.name`.
 *
 * Three sources, cheapest first — the same escalation
 * `hydrateScheduleRowsTimezoneSource` uses for `users.timezone`, and for the
 * same reason: a direct client read only sees what RLS lets the VIEWER see, and
 * a schedule row's owner is very often somebody else.
 *
 *   1. `<row schema>.users` — a direct read in the schema the row came from.
 *      Cross-schema charts (a patient opened from a linked provider) must look
 *      the creator up THERE, not in the viewer's home schema, or the id simply
 *      isn't found.
 *   2. `public.users` — legacy/staging rows whose owner only exists in public.
 *   3. `/api/schema-users/` — service-role listing for that schema, which also
 *      backfills name/email from Auth for rows the users table never got.
 *
 * Results are memoized per (schema, user id) for the lifetime of the tab:
 * creator names don't change during a session, and both chart pages would
 * otherwise re-query on every remount.
 */
import { getSupabase } from '../components/supabaseClient';
import { getBackendURL } from '../components/env';

const cache = new Map();

const keyFor = (schema, userId) => `${schema || ''}:${userId || ''}`;

/** name → email → null. Never returns an empty string. */
function pickDisplayName(row) {
  if (!row) return null;
  const name = String(row.name || '').trim();
  if (name) return name;
  const email = String(row.email || '').trim();
  return email || null;
}

async function readUsersRow(supabase, schema, userId) {
  try {
    const client = schema ? supabase.schema(schema) : supabase;
    const { data } = await client
      .from('users')
      .select('id, name, email')
      .eq('id', userId)
      .maybeSingle();
    return pickDisplayName(data);
  } catch (_) {
    return null;
  }
}

async function readViaBackend(supabase, schema, userId) {
  try {
    const { data: { session } = {} } = await supabase.auth.getSession();
    const viewerId = session?.user?.id;
    if (!viewerId) return null;
    const params = new URLSearchParams({ user_id: viewerId });
    if (schema) params.set('schema', schema);
    const res = await fetch(`${getBackendURL()}/api/schema-users/?${params.toString()}`);
    if (!res.ok) return null;
    const json = await res.json().catch(() => ({}));
    const match = (Array.isArray(json.users) ? json.users : []).find(
      (u) => String(u?.id) === String(userId),
    );
    return pickDisplayName(match);
  } catch (_) {
    return null;
  }
}

/**
 * @param {string|null|undefined} userId  the row's `user_id`
 * @param {string|null|undefined} schema  the schema the row was read from
 * @returns {Promise<string|null>} display name, or null when it can't be resolved
 */
export async function fetchRowCreatorName(userId, schema) {
  if (!userId) return null;
  const key = keyFor(schema, userId);
  if (cache.has(key)) return cache.get(key);

  const supabase = await getSupabase();
  let name = await readUsersRow(supabase, schema, userId);
  if (!name && schema !== 'public') name = await readUsersRow(supabase, 'public', userId);
  if (!name) name = await readViaBackend(supabase, schema, userId);

  // Only successes are memoized — a transient RLS/network miss shouldn't
  // pin "unknown" onto this creator for the rest of the session.
  if (name) cache.set(key, name);
  return name;
}

export function __clearRowCreatorCacheForTests() {
  cache.clear();
}
